import {calculateErrorMessage} from 'common/helpers'

export interface ComponentRouteInfo {
  componentName: string
  route: string
  componentRootSelector: string
  readinessValidators: string[]
  dataLoadingIndicators: string[]
  mountTimeout: number
  dataLoadTimeout: number
  observerTimeout?: number
}

function normalizePath(path: string): string {
  try {
    if (!path) return ''
    const withoutHash = String(path).split('#')[0]
    let decoded = decodeURIComponent(withoutHash)
    if (decoded.length > 1 && decoded.endsWith('/')) decoded = decoded.slice(0, -1)
    return decoded
  } catch (e) {
    calculateErrorMessage(e)
    return String(path)
  }
}

const STATIC_ROUTE_REGISTRY: Record<string, ComponentRouteInfo> = {
  'onboarding.cst': {
    componentName: 'CstUploadPage',
    route: '/institute/data-import/class-subject-teacher/upload',
    componentRootSelector: "select[name='branchId']",
    readinessValidators: ["select[name='branchId']"],
    dataLoadingIndicators: ['.spinner-border', '.loading-spinner', '.skeleton'],
    mountTimeout: 12000,
    dataLoadTimeout: 15000,
    observerTimeout: 12000,
  },
  'onboarding.sp': {
    componentName: 'StudentParentUploadPage',
    route: '/institute/data-import/student-parent/upload',
    componentRootSelector: "select[name='branchId']",
    readinessValidators: ["select[name='branchId']"],
    dataLoadingIndicators: ['.spinner-border', '.loading-spinner', '.skeleton'],
    mountTimeout: 12000,
    dataLoadTimeout: 15000,
    observerTimeout: 12000,
  },
}

export const WALKTHROUGH_ROUTE_REGISTRY: Record<string, ComponentRouteInfo> = {}

export function populateRegistryFromDOM() {
  const selectors = ['[data-walkthrough-step]', '.walkthrough-root', '.card', '.root']

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el, idx) => {
      const componentName =
        (el as HTMLElement).getAttribute('data-component-name') || `Component${idx + 1}`
      const stepId =
        (el as HTMLElement).getAttribute('data-walkthrough-step') || componentName.toLowerCase()
      if (!WALKTHROUGH_ROUTE_REGISTRY[stepId]) {
        WALKTHROUGH_ROUTE_REGISTRY[stepId] = {
          componentName,
          route: window.location.pathname,
          componentRootSelector: selector,
          readinessValidators: [selector],
          dataLoadingIndicators: ['.spinner-border', '.loading-spinner', '.skeleton'],
          mountTimeout: 8000,
          dataLoadTimeout: 10000,
        }
      }
    })
  })
}

populateRegistryFromDOM()

export function getComponentFromStepId(stepId: string): ComponentRouteInfo | null {
  if (!stepId || typeof stepId !== 'string') {
    return null
  }
  const parts = stepId.split('.').filter(Boolean)
  const prefixTwo = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0]
  const candidates = [stepId, prefixTwo, parts[0]].filter(Boolean)

  for (const key of candidates) {
    if (STATIC_ROUTE_REGISTRY[key]) return STATIC_ROUTE_REGISTRY[key]
    if (WALKTHROUGH_ROUTE_REGISTRY[key]) return WALKTHROUGH_ROUTE_REGISTRY[key]
  }

  const detected = detectWalkthroughComponents()
  if (detected.length > 0) {
    return detected[0]
  }

  return null
}

export function getComponentFromSteps(steps: any[]): ComponentRouteInfo | null {
  if (!steps || steps.length === 0) {
    return null
  }

  const firstStepId = steps[0]?.id
  if (!firstStepId) {
    return null
  }

  const byId = getComponentFromStepId(firstStepId)
  if (byId) return byId

  const firstWithPath = steps.find((s) => typeof s?.path === 'string' && s.path.length > 0)
  if (firstWithPath?.path) {
    const targetSelector = firstWithPath?.walkthrough?.target
    return {
      componentName: `Route:${firstWithPath.path}`,
      route: firstWithPath.path,
      componentRootSelector: targetSelector || '.page-content, .container-fluid, main, body',
      readinessValidators: targetSelector ? [targetSelector] : ['body'],
      dataLoadingIndicators: ['.spinner-border', '.loading-spinner', '.skeleton'],
      mountTimeout: 12000,
      dataLoadTimeout: 15000,
      observerTimeout: 12000,
    }
  }

  const detected = detectWalkthroughComponents()
  if (detected.length > 0) return detected[0]

  return null
}

export function isOnCorrectRoute(componentInfo: ComponentRouteInfo): boolean {
  if (!componentInfo || !componentInfo.route) {
    return false
  }

  try {
    const currentPath = normalizePath(window.location.pathname)
    const target = normalizePath(componentInfo.route)
    return currentPath === target
  } catch (e) {
    calculateErrorMessage(e)
    return false
  }
}

export function detectWalkthroughComponents(): ComponentRouteInfo[] {
  const selectors = ['[data-walkthrough-step]', '.walkthrough-root', '.card', '.root']

  const found: ComponentRouteInfo[] = []

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el, idx) => {
      const componentName =
        (el as HTMLElement).getAttribute('data-component-name') || `Component${idx + 1}`
      const route = window.location.pathname
      const componentRootSelector = selector
      const readinessValidators = [selector]
      const dataLoadingIndicators = ['.spinner-border', '.loading-spinner', '.skeleton']
      const mountTimeout = 8000
      const dataLoadTimeout = 10000

      found.push({
        componentName,
        route,
        componentRootSelector,
        readinessValidators,
        dataLoadingIndicators,
        mountTimeout,
        dataLoadTimeout,
      })
    })
  })

  return found
}

export function startFirstDetectedWalkthrough() {
  const components = detectWalkthroughComponents()
  if (components.length > 0) {
    startWalkthrough()
  }
}

function startWalkthrough() {}

export function autoSelectFirstBranch() {
  const branchDropdown = document.querySelector('select[name="branchId"]') as HTMLSelectElement
  if (branchDropdown && branchDropdown.options.length > 0) {
    branchDropdown.selectedIndex = 0
    const event = new Event('change', {bubbles: true})
    branchDropdown.dispatchEvent(event)
  }
}
